import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import API_BASE_URL from "../config/api";

// --- Styled Components ---

const Section = styled.section`
  padding: 4rem 2rem;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  text-align: center;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

const Title = styled.h2`
  font-family: "MonteCarlo", cursive;
  font-size: 3.5rem;
  color: #1CA8CB;
  margin-bottom: 0.5rem;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 2.8rem;
  }
`;

const Subtitle = styled.h3`
  font-family: "Merriweather", serif;
  font-size: 2.8rem;
  font-weight: 700;
  color: #004E61;
  margin-bottom: 3rem;
  margin-top: -15px;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 2rem;
  padding: 2rem 1rem;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; // Firefox
  
  &::-webkit-scrollbar {
    display: none; // Chrome/Safari
  }
`;

const Card = styled.div`
  flex: 0 0 auto;
  width: 280px;
  height: 400px;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1);

  &:hover {
    transform: translateY(-15px);
    box-shadow: 0 25px 50px rgba(0, 78, 97, 0.25);
  }

  @media (max-width: 480px) {
    width: 240px;
    height: 340px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.8s ease;

  ${Card}:hover & {
    transform: scale(1.15);
  }
`;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(0, 78, 97, 0.9) 0%, rgba(0, 78, 97, 0) 60%);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  padding-bottom: 30px;
  transition: all 0.4s ease;

  ${Card}:hover & {
    background: linear-gradient(to top, rgba(0, 78, 97, 1) 0%, rgba(0, 78, 97, 0.4) 100%);
    backdrop-filter: blur(2px);
    padding-bottom: 50px;
  }
`;

const StateName = styled.h4`
  font-family: "Merriweather", serif;
  color: white;
  font-size: 2rem;
  margin: 0;
  text-align: center;
  text-shadow: 0 4px 8px rgba(0,0,0,0.4);
  transform: translateY(20px);
  transition: all 0.4s ease;

  ${Card}:hover & {
    transform: translateY(-10px);
  }
`;

const SeeMoreButton = styled.button`
  background: white;
  color: #004E61;
  border: none;
  padding: 12px 32px;
  border-radius: 30px;
  font-family: "Poppins", sans-serif;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  margin-top: 15px;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.4s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.2);
  
  ${Card}:hover & {
    opacity: 1;
    transform: translateY(0);
  }

  &:hover {
    background: #1CA8CB;
    color: white;
    transform: scale(1.05);
  }
`;

const TourCategories = () => {
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API_BASE_URL}/states`)
      .then((response) => response.json())
      .then((data) => setCategories(data))
      .catch((error) => console.error("Error fetching data:", error));
  }, []);

  const handleSeeMore = (stateName) => {
    navigate("/destination", { state: { selectedState: stateName } });
  };

  return (
    <Section>
      <Title>Wonderful Place For You</Title>
      <Subtitle>State Tour</Subtitle>
      <ScrollContainer>
        {categories.map((category, index) => (
          <Card key={index} onClick={() => handleSeeMore(category.state)}>
            <CardImage src={category.stateImage} alt={category.state} />
            <Overlay>
              <StateName>{category.state}</StateName>
              <SeeMoreButton>Explore</SeeMoreButton>
            </Overlay>
          </Card>
        ))}
      </ScrollContainer>
    </Section>
  );
};

export default TourCategories;
